import { SITE_ASSESSMENT_NAMES } from "../../assessments.table/table.utils";

const UNITED_STATES = "United States";
const USA = "USA";
const SITE_COST_FLAG = "Site Cost";
const MAX_ROWS = 1000;

const extractCountryName = (sheetName) => {
  return sheetName.trim();
};

const getCellValue = (worksheet, column, row) => {
  return worksheet[`${column}${row}`]?.v;
};

const getRowData = (worksheet, row) => {
  return {
    type: getCellValue(worksheet, 'A', row),
    quantity: Number(getCellValue(worksheet, 'B', row)) || 1,
    assessmentName: getCellValue(worksheet, 'G', row),
    cost: parseFloat(getCellValue(worksheet, 'H', row)),
    siteName: getCellValue(worksheet, 'J', row),
  };
};

const isValidCost = (cost) => {
  return !isNaN(cost) && cost !== undefined;
};

const addRegularAssessment = (assessments, name, cost) => {
  if (name !== undefined && name !== null) {
    assessments.push({
      assessmentName: name,
      assessmentCost: isValidCost(cost) ? cost : null,
    });
  }
};

const addSiteAssessment = (siteAssessments, siteName, cost, quantity) => {
  if (siteName && SITE_ASSESSMENT_NAMES.includes(siteName) && isValidCost(cost)) {
    const totalCost = cost * quantity;
    siteAssessments[siteName] = (siteAssessments[siteName] || 0) + totalCost;
  }
};

const convertSiteAssessmentsToArray = (siteAssessments) => {
  return Object.entries(siteAssessments).map(([siteName, totalCost]) => ({
    assessmentName: siteName,
    assessmentCost: totalCost,
  }));
};

const processSheetData = (worksheet) => {
  const assessments = [];
  const siteAssessments = {};
  let processSiteCosts = false;

  for (let row = 1; row <= MAX_ROWS; row++) {
    const rowData = getRowData(worksheet, row);

    if (rowData.type === SITE_COST_FLAG) {
      processSiteCosts = true;
      continue;
    }

    if (!processSiteCosts) {
      // Include row if assessmentName exists, even if cost is missing
      addRegularAssessment(assessments, rowData.assessmentName, rowData.cost);
    } else {
      // Site assessments still require a valid cost to calculate totals
      if (!isValidCost(rowData.cost)) continue;
      addSiteAssessment(siteAssessments, rowData.siteName, rowData.cost, rowData.quantity);
    }
  }

  return [...assessments, ...convertSiteAssessmentsToArray(siteAssessments)];
};

const getCountriesWithExistingCosts = (SOECountries, countryNamesSet) => {
  return SOECountries.reduce((acc, soc) => {
    if (soc?.hasAssessmentCost === 1 && soc?.siteId === 1 && countryNamesSet.has(soc.siteGroupName)) {
      acc.push(soc.siteGroupName);
    }
    return acc;
  }, []);
};

export const processExcelFile = (workbook, scheduleId, SOECountries) => {
  const countryData = [];
  const countryNamesSet = new Set();
  let totalValidSheets = 0;

  workbook.SheetNames.forEach((sheetName) => {
    const countryName = extractCountryName(sheetName);
    const formattedCountryName = countryName.includes(UNITED_STATES) 
      ? countryName.replace(UNITED_STATES, USA)
      : countryName;
    countryNamesSet.add(formattedCountryName);
    const worksheet = workbook.Sheets[sheetName];
    const assessments = processSheetData(worksheet);

    if (assessments.length > 0) {
      totalValidSheets++;
      assessments.forEach((assessment) => {
        countryData.push({
          scheduleOfEventsId: scheduleId,
          countryName,
          assessmentName: assessment.assessmentName,
          assessmentCost: assessment.assessmentCost,
        });
      });
    }
  });

  const countriesWithCosts = getCountriesWithExistingCosts(SOECountries, countryNamesSet);
  return { countryData, totalValidSheets, countriesWithCosts };
};
